import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  GamepadIcon, 
  FolderOpen, 
  Users, 
  TrendingUp, 
  Settings, 
  BarChart3,
  PlusCircle,
  Eye,
  Star,
  Shield,
  Globe,
  FileText,
  Monitor,
  Search,
  ArrowUpRight
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import TopGamesManager from '@/components/admin/TopGamesManager';

const AdminDashboard = () => {
  // Fetch dashboard stats
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      // Fetch all games with pagination to avoid 1000-row limit
      const fetchAllGames = async () => {
        let allGames: any[] = [];
        let from = 0;
        const pageSize = 1000;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from('games')
            .select('id, play_count, rating')
            .eq('hidden', false)
            .range(from, from + pageSize - 1);

          if (error) throw error;
          
          if (data && data.length > 0) {
            allGames = [...allGames, ...data];
            from += pageSize;
            hasMore = data.length === pageSize;
          } else {
            hasMore = false;
          }
        }

        return allGames;
      };

      const [games, categoriesResult, adminUsersResult] = await Promise.all([
        fetchAllGames(),
        supabase.from('categories').select('id'),
        supabase.from('admin_users').select('id').eq('is_active', true),
      ]);

      const categories = categoriesResult.data || [];
      const adminUsers = adminUsersResult.data || [];
      
      // Calculate real play counts (subtract the base inflated amount)
      const totalRealPlays = games.reduce((sum, game) => {
        const realPlays = Math.max(0, (game.play_count || 0) - 5000);
        return sum + realPlays;
      }, 0);
      
      const avgRating = games.length > 0 
        ? games.reduce((sum, game) => sum + (game.rating || 0), 0) / games.length 
        : 0;

      return {
        totalGames: games.length,
        totalCategories: categories.length,
        totalRealPlays,
        avgRating: avgRating.toFixed(1),
        totalAdmins: adminUsers.length,
      };
    },
  });

  // Fetch recent games with real play counts
  const { data: recentGames = [] } = useQuery({
    queryKey: ['recent-games'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games')
        .select('id, title, category, play_count, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      
      // Calculate real play counts for display
      return data.map(game => ({
        ...game,
        real_play_count: Math.max(0, (game.play_count || 0) - 5000)
      }));
    },
  });

  const dashboardStats = [
    { 
      label: 'Total Games', 
      value: stats?.totalGames || '0', 
      icon: GamepadIcon, 
      color: 'from-blue-500 to-blue-600',
      lightColor: 'from-blue-50 to-blue-100',
      href: '/admin/games',
      change: '+12%'
    },
    { 
      label: 'Categories', 
      value: stats?.totalCategories || '0', 
      icon: FolderOpen, 
      color: 'from-emerald-500 to-emerald-600',
      lightColor: 'from-emerald-50 to-emerald-100',
      href: '/admin/categories',
      change: '+5%'
    },
    { 
      label: 'Real Plays', 
      value: stats?.totalRealPlays?.toLocaleString() || '0', 
      icon: Eye, 
      color: 'from-purple-500 to-purple-600',
      lightColor: 'from-purple-50 to-purple-100',
      href: '/admin/analytics',
      change: '+23%'
    },
    { 
      label: 'Admin Users', 
      value: stats?.totalAdmins || '0', 
      icon: Shield, 
      color: 'from-orange-500 to-orange-600',
      lightColor: 'from-orange-50 to-orange-100',
      href: '/admin/users',
      change: '+2%'
    },
  ];

  const quickActions = [
    { title: 'Add New Game', icon: PlusCircle, href: '/admin/games', color: 'from-blue-500 to-blue-600' },
    { title: 'Manage Categories', icon: FolderOpen, href: '/admin/categories', color: 'from-emerald-500 to-emerald-600' },
    { title: 'View Analytics', icon: BarChart3, href: '/admin/analytics', color: 'from-purple-500 to-purple-600' },
    { title: 'Website Settings', icon: Settings, href: '/admin/settings', color: 'from-orange-500 to-orange-600' },
    { title: 'Manage Pages', icon: FileText, href: '/admin/pages', color: 'from-pink-500 to-pink-600' },
    { title: 'SEO Settings', icon: Search, href: '/admin/seo', color: 'from-indigo-500 to-indigo-600' },
    { title: 'Ads Management', icon: Monitor, href: '/admin/ads', color: 'from-red-500 to-red-600' },
    { title: 'User Management', icon: Users, href: '/admin/users', color: 'from-cyan-500 to-cyan-600' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome to your admin dashboard. Here's an overview of your gaming platform.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {[
          { 
            label: 'Total Games', 
            value: stats?.totalGames || '0', 
            icon: GamepadIcon, 
            color: 'from-blue-500 to-blue-600',
            lightColor: 'from-blue-50 to-blue-100',
            href: '/admin/games',
            change: '+12%'
          },
          { 
            label: 'Categories', 
            value: stats?.totalCategories || '0', 
            icon: FolderOpen, 
            color: 'from-emerald-500 to-emerald-600',
            lightColor: 'from-emerald-50 to-emerald-100',
            href: '/admin/categories',
            change: '+5%'
          },
          { 
            label: 'Real Plays', 
            value: stats?.totalRealPlays?.toLocaleString() || '0', 
            icon: Eye, 
            color: 'from-purple-500 to-purple-600',
            lightColor: 'from-purple-50 to-purple-100',
            href: '/admin/analytics',
            change: '+23%'
          },
          { 
            label: 'Admin Users', 
            value: stats?.totalAdmins || '0', 
            icon: Shield, 
            color: 'from-orange-500 to-orange-600',
            lightColor: 'from-orange-50 to-orange-100',
            href: '/admin/users',
            change: '+2%'
          },
        ].map((stat, index) => (
          <Link key={index} to={stat.href} className="group">
            <Card className="p-6 hover:shadow-xl transition-all duration-500 cursor-pointer border-0 bg-white/80 backdrop-blur-sm hover:bg-white group-hover:scale-105 transform">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.lightColor} group-hover:scale-110 transition-transform duration-300`}>
                  <stat.icon className={`w-6 h-6 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`} />
                </div>
                <div className="flex items-center space-x-1 text-emerald-600">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm font-medium">{stat.change}</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900">{isLoading ? '...' : stat.value}</p>
              </div>
              <div className="mt-4 flex items-center text-gray-500 group-hover:text-blue-600 transition-colors">
                <span className="text-sm">View Details</span>
                <ArrowUpRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Top Games Management */}
      <TopGamesManager />

      {/* Quick Actions */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-sm">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-2 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-3"></div>
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.href}
              className="group relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-white to-gray-50/50 border border-gray-200/50 hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105"
            >
              <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${action.color} text-white mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                <action.icon className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                {action.title}
              </h3>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 rounded-2xl"></div>
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ArrowUpRight className="w-5 h-5 text-blue-600" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Recent Games */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm border-0 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <div className="w-2 h-5 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full mr-3"></div>
              Recent Games
            </h3>
            <Link to="/admin/games" className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center group">
              View All
              <ArrowUpRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
          <div className="space-y-4">
            {recentGames.length === 0 ? (
              <div className="text-center py-8">
                <GamepadIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No games added yet</p>
              </div>
            ) : (
              recentGames.map((game) => (
                <div key={game.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-blue-50/50 rounded-xl hover:shadow-md transition-all duration-300 group">
                  <div>
                    <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{game.title}</p>
                    <p className="text-sm text-gray-600">{game.category}</p>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500 bg-white/50 rounded-lg px-3 py-1">
                    <Eye className="w-4 h-4" />
                    <span>{game.real_play_count.toLocaleString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Platform Overview */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm border-0 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <div className="w-2 h-5 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full mr-3"></div>
              Platform Overview
            </h3>
            <Link to="/admin/analytics" className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center group">
              View Analytics
              <ArrowUpRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
          <div className="space-y-4">
            {[
              { label: 'Published Games', value: stats?.totalGames || 0, icon: GamepadIcon, color: 'text-blue-600' },
              { label: 'Active Categories', value: stats?.totalCategories || 0, icon: FolderOpen, color: 'text-emerald-600' },
              { label: 'Real Plays', value: stats?.totalRealPlays?.toLocaleString() || 0, icon: Eye, color: 'text-purple-600' },
              { label: 'Average Rating', value: `${stats?.avgRating || '0.0'}/5`, icon: Star, color: 'text-yellow-600' },
              { label: 'Admin Users', value: stats?.totalAdmins || 0, icon: Shield, color: 'text-orange-600' },
            ].map((item, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-xl hover:shadow-sm transition-all duration-300">
                <div className="flex items-center space-x-3">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
                <span className="font-semibold text-gray-900">{item.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
